/**
 * Types and functions for working with boundary representation models (shortened BRep).
 * @module
 */
import type { AABB3 } from "./aabb3Types";
import { aabb3New } from "./aabb3Types";
import { classifyContainmentPolyLine2PolyLine2Fast, classifyContainmentVec2PolyLine2Fast } from "./containment";
import { ANGLE_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED } from "./core";
import { intersectRay3PlaneWithNormal } from "./intersectRay3";
import {
  planeByThreePoints,
  planeCopy,
  planeDistanceTo,
  planeEval,
  planeNew,
  planeNewXY,
  planeNewXYNeg,
  planeNewXZ,
  planeNewXZNeg,
  planeNewYZ,
  planeNewYZNeg,
  planeNormal,
  planeProject,
  planeProjectPolyLine3,
  planeReProjectPolyLine2,
  polyLine2OnPlane,
} from "./plane";
import type { Plane } from "./planeTypes";
import { PolygonRoundingMode, polygonUnion } from "./polyclip/polyClip";
import { polygonIntersectSimple } from "./polygon";
import {
  polyLine2AABB2,
  polyLine2Area,
  polyLine2Centroid,
  polyLine2Copy,
  polyLine2DivideByMaximumLength,
  polyLine2MakeAxisAlignedRect,
  polyLine2ReflectOverYAxis,
  polyLine2Reverse,
  polyLine2SetCCW,
  polyLine2SetCW,
  polyLine2Simplify,
} from "./polyLine2";
import type { PolyLine2, PolyLine2Polygon } from "./polyLine2Types";
import { polyLine2New } from "./polyLine2Types";
import { polyLine3EdgeCount, type PolyLine3 } from "./polyLine3";
import { vec3CrossNormalize, vec3Dot, vec3Len, vec3Normalize, type Vec3 } from "./vec3";

const { min, max } = Math;

const COPLANAR_EPSILON = 1e-3;

export type FaceTagBase = {};

export function faceTagHash(tag: FaceTagBase): string {
  let hash = "";
  for (const key of Object.keys(tag).sort()) {
    hash += `${key}:${JSON.stringify(tag[key])};`;
  }
  return hash;
}

/**
 * Extrusions are very common geometric types in buildings, so we have a dedicated type for this made up of the
 * profile and extrusion vector (which need not be normal to the profile plane)
 */
export interface Extrusion {
  type: "EXTRUSION";
  profile: Face;
  dirX: number;
  dirY: number;
  dirZ: number;
}

export function extrusionNew(profile: Face, dirX: number, dirY: number, dirZ: number): Extrusion {
  return {
    type: "EXTRUSION",
    profile,
    dirX,
    dirY,
    dirZ,
  };
}

export function extrusionAABB3(extrusion: Extrusion): AABB3 {
  const aabb = faceAABB3(extrusion.profile);
  if (extrusion.dirX < 0) {
    aabb.minX -= extrusion.dirX;
  } else {
    aabb.maxX += extrusion.dirX;
  }
  if (extrusion.dirY < 0) {
    aabb.minY -= extrusion.dirY;
  } else {
    aabb.maxY += extrusion.dirY;
  }
  if (extrusion.dirZ < 0) {
    aabb.minZ -= extrusion.dirZ;
  } else {
    aabb.maxZ += extrusion.dirZ;
  }
  return aabb;
}

/**
 * adds reference to a FaceTag or FaceTags to a Face
 */
export function faceAddTags(tags: FaceTagBase | FaceTagBase[]): (f: Face) => Face {
  return function (f: Face): Face {
    if (!f.tags) {
      f.tags = [];
    }
    if (Array.isArray(tags)) {
      f.tags.push(...tags);
    } else {
      f.tags.push(tags);
    }
    return f;
  };
}

/**
 * Tag Faces that are not null or undefined; do not return null or undefined Faces
 */
export function tagFaces(faces: (Face | null | undefined)[], tags: FaceTagBase | FaceTagBase[]): Face[] {
  const tagged: Face[] = [];
  for (const face of faces) {
    if (!faceIsDefined(face)) {
      continue;
    }
    tagged.push(tagFace(face, tags));
  }
  return tagged;
}

export function tagFace(face: Face, tags: FaceTagBase | FaceTagBase[]): Face {
  if (!face.tags) {
    face.tags = [];
  }
  if (Array.isArray(tags)) {
    face.tags.push(...tags);
  } else {
    face.tags.push(tags);
  }
  return face;
}

/**
 * A Face is a polygon with a plane. The first PolyLine2 is the outer loop and should be CW. The rest are inner loops and are CCW.
 */
export interface Face {
  type: "FACE";
  loops: PolyLine2[];
  plane: Plane;
  tags?: FaceTagBase[];
}

export function faceNew(loops: PolyLine2[], plane: Plane, tags?: FaceTagBase[]): Face {
  console.assert(loops.length !== 0, "empty loop array is not allowed");
  return {
    type: "FACE",
    loops,
    plane,
    tags,
  };
}

export interface FaceGroup {
  type: "FACE_GROUP";
  faces: Face[];
}

export function faceGroupNew(faces: Face[]): FaceGroup {
  return {
    type: "FACE_GROUP",
    faces,
  };
}

export function faceTopoNew(): FaceTopo {
  return { loopTopos: [] };
}

export function brepNew(faces: Face[], faceTopos: FaceTopo[]): BRep {
  if (faces.length !== faceTopos.length) {
    throw new Error("Illegal face topo count");
  }
  return {
    type: "BREP",
    faces,
    faceTopos,
  };
}

export function brepCopy(brep: BRep): BRep {
  return brepNew(
    brep.faces.map(faceCopy),
    brep.faceTopos.map((x) => ({ loopTopos: x.loopTopos.slice() })),
  );
}

export function faceMove(face: Face, x: number, y: number, z: number) {
  face.plane.originX += x;
  face.plane.originY += y;
  face.plane.originZ += z;
  return face;
}

/**
 * @deprecated Use {@link faceMove} instead as this unnecessarily muddies whether the face is null downstream
 */
export function faceOrNullMove(face: Face | null, x: number, y: number, z: number) {
  if (!face) {
    return null;
  }

  return faceMove(face, x, y, z);
}

export function faceMoveAlongNormal(face: Face, dist: number) {
  const [normalX, normalY, normalZ] = planeNormal(face.plane);
  return faceMove(face, normalX * dist, normalY * dist, normalZ * dist);
}

/**
 * @deprecated Use {@link faceMoveAlongNormal} instead as this unnecessarily muddies whether the face is null downstream
 */
export function faceOrNullMoveAlongNormal(face: Face | null, dist: number) {
  if (!face) {
    return null;
  }

  return faceMoveAlongNormal(face, dist);
}

export function faceProjectOnPlane(face: Face, plane: Plane): Face {
  const { loops } = face;

  for (let i = 0, l = face.loops.length; i < l; i++) {
    const loop3 = polyLine2OnPlane(face.plane, loops[i]);
    loops[i] = planeProjectPolyLine3(plane, loop3);
  }

  face.plane = planeCopy(plane);

  return face;
}

export function faceProjectCopyOnPlane(face: Face, plane: Plane): Face {
  return faceProjectOnPlane(faceCopy(face), plane);
}

export function faceIsNotNull(face: Face | null): face is Face {
  return face !== null;
}

export function faceIsDefined(face: Face | null | undefined): face is Face {
  return face !== undefined && faceIsNotNull(face);
}

export function faceCopy(face: Face): Face {
  return faceNew(
    face.loops.map(polyLine2Copy),
    planeCopy(face.plane),
    face.tags ? [...face.tags] : undefined, // tags (immutable) are always shallow copied
  );
}

/**
 * @deprecated Use {@link faceCopy} instead as this unnecessarily muddies whether the face is null downstream
 */
export function faceOrNullCopy(face: Face | null): Face | null {
  if (!face) {
    return null;
  }
  return faceNew(
    face.loops.map(polyLine2Copy),
    planeCopy(face.plane),
    face.tags ? [...face.tags] : undefined, // tags (immutable) are always shallow copied
  );
}

export function faceReverse(face: Face): Face {
  const { loops, plane } = face;
  for (const loop of loops) {
    polyLine2Reverse(polyLine2ReflectOverYAxis(loop));
  }
  if (plane) {
    plane.uX *= -1;
    plane.uY *= -1;
    plane.uZ *= -1;
  }

  return face;
}

/**
 * @deprecated Use {@link faceReverse} instead as this unnecessarily muddies whether the face is null downstream
 */
export function faceOrNullReverse(face: Face | null): Face | null {
  if (!face) {
    return null;
  }

  return faceReverse(face);
}

export function faceRepairLoopDirections(face: Face): Face {
  const { loops } = face;
  polyLine2SetCW(loops[0]);
  for (let i = 1, l = loops.length; i < l; i++) {
    polyLine2SetCCW(loops[i]);
  }
  return face;
}

export function faceSimplifyLoops(
  face: Face,
  distThresholdSq: number | undefined = DIST_EPSILON_SQUARED,
  angleThreshold: number | undefined = ANGLE_EPSILON,
): Face {
  const { loops } = face;
  for (let i = 0; i < loops.length; i++) {
    face.loops[i] = polyLine2Simplify(loops[i], distThresholdSq, angleThreshold);
  }
  return face;
}

export function faceLoopDivideByMaximumLength(face: Face, maxLength: number, onlyFirstLoop: boolean = false) {
  const loopCount = onlyFirstLoop ? 1 : face.loops.length;
  for (let i = 0; i < loopCount; i++) {
    face.loops[i] = polyLine2DivideByMaximumLength(face.loops[i], maxLength);
  }
}

export function transformFaceLoopBasis(loop: PolyLine2, srcPlane: Plane, dstPlane: Plane) {
  const n = loop.length;
  for (let i = 0; i < n / 2; i++) {
    const [x, y, z] = planeEval(srcPlane, loop[2 * i], loop[2 * i + 1]);
    [loop[2 * i], loop[2 * i + 1]] = planeProject(dstPlane, x, y, z);
  }
  return loop;
}

export function mergeAdjacentCoplanarFaces(inputFaces: Face[]): Face | null {
  if (inputFaces.length === 0) {
    return null;
  } else if (inputFaces.length === 1) {
    return faceCopy(inputFaces[0]);
  }

  // Copy faces to transform them into common frame
  const faces = inputFaces.map((face) => faceCopy(face));
  const firstFace = faces[0];
  const startingPlane = firstFace?.plane ?? null;

  const mergedFace = startingPlane ? faceNew([], startingPlane) : null;

  // Merge outer loops
  //
  // We could implement a fast path when all outer loops are contiguous walls, using a bounding box to speed the calculation.
  // In practice, wall faces created by doors make the detection harder than it's most likely worth.
  const outerLoopPolygons = faces
    .filter(faceIsNotNull)
    .map((face) => [transformFaceLoopBasis(face.loops[0], face.plane, startingPlane as Plane)]);
  const mergedPolygon = polygonUnion({ pls: outerLoopPolygons });
  console.assert(mergedPolygon.length === 1, "Merged polygon should only have one Polyline2Polygon");

  // The merged polygon may also contain its own CCW inner loops. Fortunately, they are in the same order:
  // [outer loop, inner loop 1, inner loop 2, ....]
  for (const loop of mergedPolygon[0]) {
    mergedFace?.loops.push(loop);
  }

  // Merge incoming inner loops and update face frame
  for (const face of faces) {
    if (!face) {
      continue;
    }

    for (let i = 1; i < face.loops.length; i++) {
      mergedFace?.loops.push(transformFaceLoopBasis(face.loops[i], face.plane, startingPlane as Plane));
    }
  }

  return mergedFace;
}

export function mergeNonAdjacentCoplanarFaces(
  inputFaces: Face[],
  roundingMode: PolygonRoundingMode = PolygonRoundingMode.ROUND,
): Face[] {
  if (inputFaces.length === 0) {
    return [];
  } else if (inputFaces.length === 1) {
    const copy = faceCopy(inputFaces[0]);

    return copy ? [copy] : [];
  }

  // Transform input faces into common frame
  const faces = inputFaces.map((face) => faceCopy(face));
  const firstFace = faces[0];
  const startingPlane = firstFace?.plane ?? null;

  for (let i = 1; i < faces.length; i++) {
    const face = faces[i];
    if (!face) {
      continue;
    }
    for (let j = 0; j < face.loops.length; j++) {
      transformFaceLoopBasis(face.loops[j], face.plane, startingPlane as Plane);
    }
  }

  // Union
  const facePolygons = faces.map((face) => face?.loops ?? []);
  const mergedPolygons = polygonUnion({ pls: facePolygons, roundingMode });

  // Convert result to Face type
  const mergedFaces: Face[] = [];
  for (const mergedPolygon of mergedPolygons) {
    mergedFaces.push(faceNew(mergedPolygon, planeCopy(startingPlane as Plane)));
  }

  return mergedFaces;
}

export type LoopTopo = Int32Array;

export interface FaceTopo {
  loopTopos: LoopTopo[]; // face index, loop index, edge index
}

export interface BRep {
  type: "BREP";
  faces: Face[];
  faceTopos: FaceTopo[];
}

export function brepAdjacentFaces(brep: BRep, faceIndex: number): number[] {
  const faces: number[] = [];
  const faceMap = { [faceIndex]: true };

  function addFacesOnLoop(neighbors: Int32Array) {
    for (let i = 0, l = neighbors.length; i < l; i += 3) {
      const f = neighbors[i];

      if (!faceMap[f]) {
        faces.push(f);
        faceMap[f] = true;
      }
    }
  }

  const face = brep.faces[faceIndex];
  const faceTopo = brep.faceTopos[faceIndex];

  for (let i = 0, l = face.loops.length; i < l; i++) {
    addFacesOnLoop(faceTopo.loopTopos[i]);
  }

  return faces;
}

export function classifyContainmentFaceVec2(x: number, y: number, face: Face): boolean {
  const loops = face.loops;

  if (!classifyContainmentVec2PolyLine2Fast(x, y, loops[0])) {
    return false;
  }

  for (let i = 1, l = loops.length; i < l; i++) {
    if (classifyContainmentVec2PolyLine2Fast(x, y, loops[i])) {
      return false;
    }
  }

  return true;
}

export function classifyContainmentFacePolyLine2(pl: PolyLine2, face: Face): boolean {
  const loops = face.loops;

  if (!classifyContainmentPolyLine2PolyLine2Fast(pl, loops[0])) {
    return false;
  }

  for (let i = 1, l = loops.length; i < l; i++) {
    if (classifyContainmentPolyLine2PolyLine2Fast(pl, loops[i])) {
      return false;
    }
  }

  return true;
}

export function polyLine2AABB3OnPlane(
  pl: PolyLine2,
  { uX, uY, uZ, vX, vY, vZ, originX, originY, originZ }: Plane,
  tol: number = DIST_EPSILON,
): AABB3 {
  // init
  const u0 = pl[0];
  const v0 = pl[1];

  const x0 = originX + uX * u0 + vX * v0;
  const y0 = originY + uY * u0 + vY * v0;
  const z0 = originZ + uZ * u0 + vZ * v0;

  const aabb = aabb3New(x0 - tol, y0 - tol, z0 - tol, x0 + tol, y0 + tol, z0 + tol);

  // expand
  for (let i = 2, l = pl.length; i < l; i += 2) {
    const u = pl[i];
    const v = pl[i + 1];

    const x = originX + uX * u + vX * v;
    const y = originY + uY * u + vY * v;
    const z = originZ + uZ * u + vZ * v;

    aabb.minX = min(aabb.minX, x);
    aabb.minY = min(aabb.minY, y);
    aabb.minZ = min(aabb.minZ, z);

    aabb.maxX = max(aabb.maxX, x);
    aabb.maxY = max(aabb.maxY, y);
    aabb.maxZ = max(aabb.maxZ, z);
  }

  return aabb;
}

export function faceOutlineBandOnPlane(face: Face, z0: number, z1: number): PolyLine2Polygon {
  const { plane, loops } = face;

  const outerLoop = loops[0];
  const outerLoopAabb = polyLine2AABB2(outerLoop);

  // Retroproject AABB corners into world space
  const [z0X, z0Y] = planeEval(plane, outerLoopAabb.minX, outerLoopAabb.minY);
  const [z1X, z1Y] = planeEval(plane, outerLoopAabb.maxX, outerLoopAabb.maxY);

  // Project corners into plane
  const [z0BandU, z0BandV] = planeProject(plane, z0X, z0Y, z0);
  const [z1BandU, z1BandV] = planeProject(plane, z1X, z1Y, z1);

  return polygonIntersectSimple(polyLine2MakeAxisAlignedRect(z0BandU, z0BandV, z1BandU, z1BandV), outerLoop);
}

export function faceAABB3({ loops, plane }: Face, tol: number = DIST_EPSILON): AABB3 {
  return polyLine2AABB3OnPlane(loops[0], plane, tol);
}

export function facesAABB3(faces: Face[]): AABB3 {
  let aabb;

  for (const face of faces) {
    const outerLoop = face.loops[0];
    const { uX, uY, uZ, vX, vY, vZ, originX, originY, originZ } = face.plane;

    // init
    const u0 = outerLoop[0];
    const v0 = outerLoop[1];

    const x0 = originX + uX * u0 + vX * v0;
    const y0 = originY + uY * u0 + vY * v0;
    const z0 = originZ + uZ * u0 + vZ * v0;

    if (!aabb) {
      aabb = aabb3New(
        x0 - DIST_EPSILON,
        y0 - DIST_EPSILON,
        z0 - DIST_EPSILON,
        x0 + DIST_EPSILON,
        y0 + DIST_EPSILON,
        z0 + DIST_EPSILON,
      );
    }

    // expand
    for (let i = 0, l = outerLoop.length; i < l; i += 2) {
      const u = outerLoop[i];
      const v = outerLoop[i + 1];

      const x = originX + uX * u + vX * v;
      const y = originY + uY * u + vY * v;
      const z = originZ + uZ * u + vZ * v;

      aabb.minX = min(aabb.minX, x);
      aabb.minY = min(aabb.minY, y);
      aabb.minZ = min(aabb.minZ, z);

      aabb.maxX = max(aabb.maxX, x);
      aabb.maxY = max(aabb.maxY, y);
      aabb.maxZ = max(aabb.maxZ, z);
    }
  }

  return aabb;
}

export function brepAABB3({ faces }: BRep): AABB3 {
  return facesAABB3(faces);
}

/**
 * Ensures a closed BRep has outward-facing face normals by checking signed volume
 * and flipping all faces when the orientation is inverted.
 */
export function normalizeBRepFaceNormalsOutward(brep: BRep): void {
  const signedVolume = computeBRepSignedVolumeFromFaceContributions(brep);
  if (!Number.isFinite(signedVolume) || Math.abs(signedVolume) < 1e-6) {
    return;
  }
  // In this geometry convention, outward-oriented closed BReps produce a
  // negative signed volume under this triangulation formula.
  if (signedVolume > 0) {
    for (const face of brep.faces) {
      faceReverse(face);
    }
  }
}

export function computeBRepSignedVolumeFromFaceContributions(brep: BRep): number {
  let signedVolume = 0;
  for (const face of brep.faces) {
    signedVolume += computeFaceSignedVolumeContribution(face);
  }
  return signedVolume;
}

export function computeFaceSignedVolumeContribution(face: Face): number {
  let signedVolume = 0;
  for (const loop of face.loops) {
    const vertexCount = loop.length / 2 - 1;
    if (vertexCount < 3) {
      continue;
    }
    const u0 = loop[0];
    const v0 = loop[1];
    if (u0 === undefined || v0 === undefined) {
      continue;
    }
    const [x0, y0, z0] = planeEval(face.plane, u0, v0);
    for (let vertexIndex = 1; vertexIndex < vertexCount - 1; vertexIndex++) {
      const baseIndexA = 2 * vertexIndex;
      const baseIndexB = 2 * (vertexIndex + 1);
      const u1 = loop[baseIndexA];
      const v1 = loop[baseIndexA + 1];
      const u2 = loop[baseIndexB];
      const v2 = loop[baseIndexB + 1];
      if (u1 === undefined || v1 === undefined || u2 === undefined || v2 === undefined) {
        continue;
      }
      const [x1, y1, z1] = planeEval(face.plane, u1, v1);
      const [x2, y2, z2] = planeEval(face.plane, u2, v2);
      signedVolume += (x0 * (y1 * z2 - z1 * y2) + y0 * (z1 * x2 - x1 * z2) + z0 * (x1 * y2 - y1 * x2)) / 6;
    }
  }
  return signedVolume;
}

export function brepMove(brep: BRep, x: number, y: number, z: number): BRep {
  for (const face of brep.faces) {
    faceMove(face, x, y, z);
  }
  return brep;
}

export function faceShallowCopyWithoutHoles(face: Face): Face {
  return { type: "FACE", loops: [face.loops[0]], plane: face.plane, tags: face.tags };
}

export function faceCopyWithoutHoles(face: Face): Face {
  return {
    type: "FACE",
    loops: [polyLine2Copy(face.loops[0])],
    plane: planeCopy(face.plane),
    tags: face.tags ? [...face.tags] : undefined, // tags (immutable) are always shallow copied
  };
}

export enum SolidSide {
  INSIDE,
  OUTSIDE,
  COPLANAR,
}

// faceByFourPoints takes three points in counter clockwise order
export function faceByThreePoints(
  ox: number,
  oy: number,
  oz: number,
  ux: number,
  uy: number,
  uz: number,
  vx: number,
  vy: number,
  vz: number,
): Face | null {
  const plane = planeByThreePoints(ox, oy, oz, ux, uy, uz, vx, vy, vz);

  if (plane === null) {
    return null;
  }

  const [uu, uv] = planeProject(plane, ux, uy, uz);
  const [vu, vv] = planeProject(plane, vx, vy, vz);

  return faceNew([polyLine2New([0, 0, vu, vv, uu, uv, 0, 0])], plane);
}

// faceByFourPoints takes four points in counter clockwise order
export function faceByFourPoints(
  ox: number,
  oy: number,
  oz: number,
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
  cx: number,
  cy: number,
  cz: number,
  emitAssert: boolean = true,
): Face | null {
  const plane = planeByThreePoints(ox, oy, oz, ax, ay, az, bx, by, bz);
  if (!plane) {
    return null;
  }

  const [au, av] = planeProject(plane, ax, ay, az);
  const [bu, bv] = planeProject(plane, bx, by, bz);

  const dist = planeDistanceTo(plane, cx, cy, cz);
  if (emitAssert) {
    console.assert(dist < COPLANAR_EPSILON, "Non planar face in faceByFourPoints");
  } else if (dist > COPLANAR_EPSILON) {
    return null;
  }

  const [cu, cv] = planeProject(plane, cx, cy, cz);
  return faceNew([polyLine2New([0, 0, cu, cv, bu, bv, au, av, 0, 0])], plane);
}

/**
 * Returns faces facing outwards.
 */
export function facesByAABB3(bb: AABB3): Face[] {
  return [
    faceNew([polyLine2MakeAxisAlignedRect(bb.minX, bb.maxY, bb.maxX, bb.minY)], planeNewXY(0, 0, bb.maxZ)),
    faceNew([polyLine2MakeAxisAlignedRect(bb.minX, -bb.minY, bb.maxX, -bb.maxY)], planeNewXYNeg(0, 0, bb.minZ)),
    faceNew([polyLine2MakeAxisAlignedRect(bb.minX, bb.maxZ, bb.maxX, bb.minZ)], planeNewXZ(0, bb.minY, 0)),
    faceNew([polyLine2MakeAxisAlignedRect(-bb.minX, bb.minZ, -bb.maxX, bb.maxZ)], planeNewXZNeg(0, bb.maxY, 0)),
    faceNew([polyLine2MakeAxisAlignedRect(bb.minY, bb.maxZ, bb.maxY, bb.minZ)], planeNewYZ(bb.maxX, 0, 0)),
    faceNew([polyLine2MakeAxisAlignedRect(-bb.minY, bb.minZ, -bb.maxY, bb.maxZ)], planeNewYZNeg(bb.minX, 0, 0)),
  ];
}

export function facesByFourPoints(
  ox: number,
  oy: number,
  oz: number,
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
  cx: number,
  cy: number,
  cz: number,
): Face[] {
  const plane = planeByThreePoints(ox, oy, oz, ax, ay, az, bx, by, bz);
  if (!plane) {
    return [];
  }

  const [au, av] = planeProject(plane, ax, ay, az);
  const [bu, bv] = planeProject(plane, bx, by, bz);

  const dist = planeDistanceTo(plane, cx, cy, cz);
  if (dist < COPLANAR_EPSILON) {
    const [cu, cv] = planeProject(plane, cx, cy, cz);
    return [faceNew([polyLine2New([0, 0, cu, cv, bu, bv, au, av, 0, 0])], plane)];
  } else {
    const face1 = faceByThreePoints(ox, oy, oz, ax, ay, az, bx, by, bz);
    const face2 = faceByThreePoints(ox, oy, oz, bx, by, bz, cx, cy, cz);

    return face1 && face2 ? [face1, face2] : [];
  }
}

export function faceByNPoints(p: ArrayLike<number>): Face | null {
  console.assert(p.length % 3 === 0, "faceByNPoints needs points with 3 components");
  console.assert(p.length >= 9, "faceByNPoints needs at least 3 points with 3 components each");

  const plane = planeByThreePoints(p[0], p[1], p[2], p[3], p[4], p[5], p[6], p[7], p[8]);
  if (!plane) {
    return null;
  }

  const [au, av] = planeProject(plane, p[3], p[4], p[5]);
  const [bu, bv] = planeProject(plane, p[6], p[7], p[8]);

  const loop = polyLine2New((p.length / 3) * 2 + 2);
  let loopPos = 0;
  loop[loopPos++] = 0;
  loop[loopPos++] = 0;
  loop[loopPos++] = au;
  loop[loopPos++] = av;
  loop[loopPos++] = bu;
  loop[loopPos++] = bv;

  for (let i = 9, l = p.length; i < l; i += 3) {
    console.assert(
      planeDistanceTo(plane, p[i], p[i + 1], p[i + 2]) < COPLANAR_EPSILON,
      "Non planar face in faceByNPoints",
    );

    const [pu, pv] = planeProject(plane, p[i], p[i + 1], p[i + 2]);

    loop[loopPos++] = pu;
    loop[loopPos++] = pv;
  }

  loop[loopPos++] = 0;
  loop[loopPos++] = 0;

  return faceNew([polyLine2Reverse(loop)], plane);
}

/**
 * Generate a face from each polyline segment.
 * @param polyline3 Polyline that the rectangular faces will follow.
 * @param width Width of the faces.
 * @param normalX The orientation of the faces will be as close as possible to this normal.
 * @param normalY
 * @param normalZ
 * @param fallbackNormalX In case the normal is collinear with a polyline segment, use this fallback normal.
 * @param fallbackNormalY
 * @param fallbackNormalZ
 * @returns
 */
export function facesByPolyline3Segments(
  polyline3: PolyLine3,
  width: number,
  normalX = 0,
  normalY = 0,
  normalZ = 1,
  fallbackNormalX = 0,
  fallbackNormalY = 1,
  fallbackNormalZ = 0,
) {
  const faces: Face[] = [];
  const edgeCount = polyLine3EdgeCount(polyline3);
  for (let i = 0; i < edgeCount; ++i) {
    const x = polyline3[i * 3];
    const y = polyline3[i * 3 + 1];
    const z = polyline3[i * 3 + 2];
    const x2 = polyline3[i * 3 + 3];
    const y2 = polyline3[i * 3 + 4];
    const z2 = polyline3[i * 3 + 5];
    const xDelta = x2 - x;
    const yDelta = y2 - y;
    const zDelta = z2 - z;
    const deltaLen = vec3Len(xDelta, yDelta, zDelta);
    if (deltaLen > DIST_EPSILON) {
      const [uX, uY, uZ] = vec3Normalize(xDelta, yDelta, zDelta);
      const [vX, vY, vZ] =
        vec3Dot(normalX, normalY, normalZ, uX, uY, uZ) < 0.99
          ? vec3CrossNormalize(xDelta, yDelta, zDelta, normalX, normalY, normalZ)
          : vec3CrossNormalize(xDelta, yDelta, zDelta, fallbackNormalX, fallbackNormalY, fallbackNormalZ);
      const loop = polyLine2MakeAxisAlignedRect(0, width / 2, deltaLen, -width / 2);
      const plane = planeNew(x, y, z, uX, uY, uZ, -vX, -vY, -vZ);
      faces.push(faceNew([loop], plane));
    }
  }
  return faces;
}

export function faceArea(face: Face | null | undefined): number {
  if (!face) {
    return 0;
  }
  let area = polyLine2Area(face.loops[0]);
  for (let i = 1, ic = face.loops.length; i < ic; i++) {
    area -= polyLine2Area(face.loops[i]);
  }
  return area;
}

export function facesArea(faces: Face[]) {
  return faces.reduce((current, f) => faceArea(f) + current, 0);
}

export function faceProjectOuterLoopToPlane(face: Face, plane: Plane): PolyLine2 {
  return planeReProjectPolyLine2(face.loops[0], face.plane, plane);
}

export function projectWorldXYPolyLine2ToFaceUV(outline: PolyLine2, face: Face): PolyLine2 {
  const { plane } = face;
  const [nX, nY, nZ] = planeNormal(plane);
  const out = polyLine2New(outline.length);
  for (let i = 0, l = outline.length; i < l; i += 2) {
    const x = outline[i];
    const y = outline[i + 1];
    const res = intersectRay3PlaneWithNormal(x, y, 0, 0, 0, 1, plane.originX, plane.originY, plane.originZ, nX, nY, nZ);
    const z = res === null ? 0 : res[2];
    const [u, v] = planeProject(plane, x, y, z);
    out[i] = u;
    out[i + 1] = v;
  }
  return out;
}

export function verticalFacesByPolyLine3AndBottomZ(
  polyline: PolyLine3,
  bottomZ: number,
  flipFaces: boolean = false,
): Face[] {
  const faces: Face[] = [];

  const lastEdgeIndex = polyline.length - 3;

  for (let i = 0; i <= lastEdgeIndex - 3; i += 3) {
    const x0 = polyline[i];
    const y0 = polyline[i + 1];
    const z0 = polyline[i + 2];
    const x1 = polyline[i + 3];
    const y1 = polyline[i + 4];
    const z1 = polyline[i + 5];

    // Skip degenerate world XY edges
    if (Math.abs(x0 - x1) <= DIST_EPSILON && Math.abs(y0 - y1) <= DIST_EPSILON) {
      continue;
    }

    const face = flipFaces
      ? faceByFourPoints(x0, y0, bottomZ, x0, y0, z0, x1, y1, z1, x1, y1, bottomZ, false)
      : faceByFourPoints(x0, y0, bottomZ, x1, y1, bottomZ, x1, y1, z1, x0, y0, z0, false);
    if (face) {
      faces.push(face);
    }
  }

  return faces;
}

export function faceCentroid(face: Face): Vec3 | null {
  if (face.loops.length < 1) {
    return null;
  }

  const c = polyLine2Centroid(face.loops[0]);
  if (!c) {
    return null;
  }

  return planeEval(face.plane, c[0], c[1]);
}
